/**
 * The states every screen has to render (task 47): loading, error and
 * empty, plus the badges the alert panel shows. Shared so the wording and
 * the look are the same everywhere, and so an error panel always shows the
 * correlation id the API handed back. "No Client selected" stays in the
 * app, because it links into MDR's router.
 */

import type { ReactNode } from 'react';

import { describeError } from '@bluesphere/api-client';
import { PillButton, colors } from '@bluesphere/ui-kit';

export function Loading({ height = 'h-28' }: { height?: string }) {
  return (
    <div className={`flex items-center justify-center ${height}`} role="status" aria-live="polite">
      <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-bluesphere-primary" />
      <span className="sr-only">Loading</span>
    </div>
  );
}

export function ErrorState({
  title = 'Could not load this',
  error,
  onRetry,
}: {
  title?: string;
  error: unknown;
  onRetry?: () => void;
}) {
  return (
    <div className="bs-card border border-red-200 bg-red-50 p-6" role="alert">
      <div className="text-sm font-semibold text-red-900">{title}</div>
      <div className="mt-1 wrap-break-word font-mono text-xs text-red-800">{describeError(error)}</div>
      {onRetry && (
        <div className="mt-3">
          <PillButton variant="outline" size="sm" onClick={onRetry}>
            Try again
          </PillButton>
        </div>
      )}
    </div>
  );
}

export function EmptyState({
  title,
  body,
  action,
}: {
  title: string;
  body?: string;
  action?: ReactNode;
}) {
  return (
    <div className="flex flex-col items-center justify-center gap-3 py-16 text-center">
      <p className="text-sm font-semibold text-bluesphere-textOnLight">{title}</p>
      {body && <p className="max-w-sm text-sm text-bluesphere-muted">{body}</p>}
      {action}
    </div>
  );
}

export function HealthChip({ status }: { status: 'healthy' | 'degraded' | 'pending' | string }) {
  const config =
    {
      healthy: { label: 'Healthy', color: colors.success, tint: '#E5F8F3' },
      degraded: { label: 'Degraded', color: colors.scoreHigh, tint: colors.tintHigh },
      pending: { label: 'Pending', color: colors.muted, tint: '#F1F2F4' },
    }[status] ?? { label: status, color: colors.muted, tint: '#F1F2F4' };

  return (
    <span className="bs-chip" style={{ backgroundColor: config.tint, color: '#2A3241' }}>
      <span
        aria-hidden="true"
        className="inline-block h-2 w-2 rounded-full"
        style={{ backgroundColor: config.color }}
      />
      {config.label}
    </span>
  );
}

const GROUP_NAMES: Record<string, string> = {
  vip: 'VIP',
  administrator: 'Administrator',
  external: 'External',
  technical: 'Technical',
  financial: 'Financial',
  nhi: 'NHI',
  'critical-asset': 'Critical Asset',
  'domain-controller': 'Domain Controller',
  server: 'Server',
  workstation: 'Workstation',
  mobile: 'Mobile',
};

export function groupName(slug: string): string {
  return GROUP_NAMES[slug] ?? slug.replaceAll('-', ' ');
}

/** A system-group membership (Story 15). Display only: no policy hangs off it in the MVP. */
export function GroupBadge({ slug }: { slug: string }) {
  return (
    <span
      className="rounded-full border border-bluesphere-border px-2 py-0.5 text-xs font-medium text-bluesphere-textSecondary"
      title="System group"
    >
      {groupName(slug)}
    </span>
  );
}

/** Whose row this is, on the all-Clients queue and stream (task 66). */
export function ClientBadge({ name }: { name: string }) {
  return (
    <span
      className="max-w-48 truncate rounded-full border border-bluesphere-primary/30 bg-white px-2 py-0.5 text-xs font-semibold text-bluesphere-primary"
      title={name}
    >
      {name}
    </span>
  );
}

export function SourceBadge({ label }: { label: string }) {
  return (
    <span className="rounded-full bg-bluesphere-surfaceAlt px-2 py-0.5 text-xs font-medium text-bluesphere-textSecondary">
      {label}
    </span>
  );
}
